import { Layout, Table, Typography } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { formatDataGeneral, getFileLinkIfExist } from "utils/utils";
import {
  isEvidenceLinkUploaded,
  renderDpatEvidenceLink,
} from "utils/renderDpatEvidenceLink";
import { documentsColumn } from "utils/tableColums";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";

const DOCUMENT_HUB_FILE_ELEMENT = "MjPmcbueEdU";
const AUDITOR_GENERAL_REPORT_TYPE = "Auditor-General Reports";

const EMPTY_IRREGULARITIES = {
  dataSource: [],
  cashIrreguarityPercentage: "0.00",
  otherIrreguaritiesPercentage: "0.00",
  onePercentTotalExpendature: 0,
  threePercentTotalExpendature: 0,
  onePercentTotalExpendatureDisplay: "GH₵ 0.00",
  threePercentTotalExpendatureDisplay: "GH₵ 0.00",
};

function parseAmount(value) {
  if (value == null || value === "" || value === "N/A") {
    return 0;
  }

  const parsed = parseFloat(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function safePercentage(part, total) {
  const numerator = parseAmount(part);
  const denominator = parseAmount(total);

  if (denominator <= 0) {
    return 0;
  }

  const result = (numerator / denominator) * 100;
  return Number.isFinite(result) ? Number(result.toFixed(2)) : 0;
}

function formatPercentageDisplay(value) {
  const numeric = parseAmount(value);
  return Number.isFinite(numeric) ? numeric.toFixed(2) : "0.00";
}

function formatGhanaCedis(amount) {
  const value = parseAmount(amount);
  return `GH₵ ${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fileUrl(eventId, dataElementUid) {
  return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementUid}`;
}

function buildAuditorGeneralReportLinks(documents) {
  const documentData = documents?.data;
  const documentReports = documents?.reports;
  const matchedDocs = formatDataGeneral(
    documentData,
    "DPAT | M&E Tool Type",
    AUDITOR_GENERAL_REPORT_TYPE,
  ) || [];

  let reportLink = "";
  if (matchedDocs.length > 0) {
    reportLink = getFileLinkIfExist(
      documentReports,
      DOCUMENT_HUB_FILE_ELEMENT,
      matchedDocs[0]?.trackedEntity,
    );
  }

  return [
    {
      title: AUDITOR_GENERAL_REPORT_TYPE,
      link: reportLink
        ? fileUrl(reportLink, DOCUMENT_HUB_FILE_ELEMENT)
        : "Not Uploaded",
    },
  ];
}

function hasAuditorGeneralReport(links = []) {
  return links.some((item) => isEvidenceLinkUploaded(item?.link));
}

const dataElementsCashIrregularities = [
  { id: 'lJr4s9aSYqF', name: 'DACF - Under Cash Irregularities' },
  { id: 'qgi5fG0sEws', name: 'DACF-RFG (DDF) - Under Cash Irregularities' },
  { id: 'I4awAW0XgZq', name: 'IGF - Under Cash Irregularities' },
];

const dataElementsContractMgntIrregularities = [
  { id: 'oqCOLV3vGl8', name: 'DACF - Under Contract Mgt' },
  { id: 'xDlsllqvOHf', name: 'DACF-RFG(DDF) - Under Contract Mgt' },
  { id: 'jZZ128qubhV', name: 'IGF - Under Contract Mgt' },
];

const dataElementsProcurementIrregularities = [
  { id: 'XRd8Sug4xJd', name: 'DACF - Under Procurement & Stores' },
  { id: 'oskeWAVWAeq', name: 'DACF-RFG (DDF) - Under Procurement & Stores' },
  { id: 'eElDNWrVib7', name: 'IGF - Under Procurement & Stores' },
];

const dataElementsAuditReport = [
  { id: 'zfKS4ps2CkO', name: 'Auditor - Total Expenditure' },
  { id: 'LRTumwNAO2X', name: 'Auditor - Percentage of other irregularities' },
  { id: 'eElDNWrVib7', name: 'IGF - Under Procurement & Stores' },
];

const AuditInfractions = forwardRef(({
  year,
  districtId,
  documents,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const { Header, Content } = Layout;
  const { Title, Text } = Typography;
  const [irregularities, setIrregularities] = useState(EMPTY_IRREGULARITIES);
  const [documentLinks, setDocumentLinks] = useState([]);
  const [scorei, setScoreI] = useState(0);
  const [scoreii, setScoreII] = useState(0);
  const [scoreiii, setScoreIII] = useState(0);
  const [maxScore, setMaxScore] = useState(6);

  const liveDocumentLinks = useMemo(
    () => buildAuditorGeneralReportLinks(documents),
    [documents],
  );

  const displayDocumentLinks = isAssessmentStarted && savedAssessmentData?.links?.length
    ? savedAssessmentData.links
    : documentLinks.length
      ? documentLinks
      : liveDocumentLinks;

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "PI3",
      area: "Audit Performance",
      maxScore,
      irregularities,
      links: displayDocumentLinks,
      scorei,
      scoreii,
      scoreiii,
    }),
  }));

  const DPAT_SLUG = "pi3.0-3.2";

  const irregularityColumns = [
    {
      title: 'Description',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '',
      dataIndex: 'value',
      key: 'value',
      align: 'right',
      render: (val) => {
        if (val === "" || val == null) {
          return "";
        }

        const numeric = parseAmount(val);
        return numeric.toLocaleString(undefined, { minimumFractionDigits: 2 });
      },
    },
  ];

  const documentTableColumns = useMemo(
    () => documentsColumn.map((col) =>
      col.dataIndex === "link"
        ? {
            ...col,
            render: (value) => renderDpatEvidenceLink(value, "View Document"),
          }
        : col
    ),
    [],
  );

  const loadFromDhis2 = useCallback(() => {
    const auditorLinks = buildAuditorGeneralReportLinks(documents);
    setDocumentLinks(auditorLinks);
    const auditorScore = hasAuditorGeneralReport(auditorLinks) ? 2 : 0;
    setScoreII(auditorScore);

    if (!districtId || year == null) {
      setIrregularities(EMPTY_IRREGULARITIES);
      setScoreI(0);
      setScoreIII(0);
      return;
    }

    axios.get(
      `/tracker/events?program=Z3qMezPtpEb&orgUnit=${districtId}&startDate=${year}-01-01&endDate=${year}-12-31`)
      .then((res) => {
        const data = res.data?.instances?.[0]?.dataValues;

        if (!Array.isArray(data) || !data.length) {
          setIrregularities(EMPTY_IRREGULARITIES);
          setScoreI(0);
          setScoreIII(0);
          return;
        }

        const mapIrregularities = (elements) => (
          elements.map((item) => {
            const match = data.find((d) => d.dataElement === item.id);
            return {
              ...item,
              value: parseAmount(match?.value),
            };
          })
        );

        const auditReport = dataElementsAuditReport.map((item) => {
          const match = data.find((d) => d.dataElement === item.id);
          return {
            ...item,
            value: parseAmount(match?.value),
          };
        });

        const cashIrregularities = mapIrregularities(dataElementsCashIrregularities);
        const contractMgntIrregularities = mapIrregularities(dataElementsContractMgntIrregularities);
        const procurementIrregularities = mapIrregularities(dataElementsProcurementIrregularities);

        const totalCash = cashIrregularities.reduce((sum, item) => sum + item.value, 0);
        const totalContract = contractMgntIrregularities.reduce((sum, item) => sum + item.value, 0);
        const totalProcurement = procurementIrregularities.reduce((sum, item) => sum + item.value, 0);
        const totalOther = totalContract + totalProcurement;
        const totalExpenditure = auditReport[0]?.value ?? 0;

        const cashIrreguarityPercentage = safePercentage(totalCash, totalExpenditure);
        const otherIrreguaritiesPercentage = safePercentage(totalOther, totalExpenditure);
        const onePercentTotalExpendature = parseAmount(totalExpenditure) * 0.01;
        const threePercentTotalExpendature = parseAmount(totalExpenditure) * 0.03;

        setScoreI(cashIrreguarityPercentage <= 1 ? 1 : 0);
        setScoreIII(otherIrreguaritiesPercentage < 3 ? 3 : 0);

        const dataSource = [
          { key: 'header1', name: <strong>Cash Irregularities</strong>, value: '', isHeader: true },
          ...cashIrregularities.map((item, index) => ({
            key: `cash-${index}`,
            name: item.name,
            value: item.value,
          })),
          {
            key: 'total-cash',
            name: <strong>Total Cash Irregularities (A)</strong>,
            value: totalCash,
          },
          { key: 'header2', name: <strong>Other irregularities</strong>, value: '', isHeader: true },
          { key: 'contract-header', name: 'Contract Mgt Irregularities', value: '' },
          ...contractMgntIrregularities.map((item, index) => ({
            key: `contract-${index}`,
            name: item.name,
            value: item.value,
          })),
          {
            key: 'contract-total',
            name: <strong>Total Contract Management irregularities (x)</strong>,
            value: totalContract,
          },
          { key: 'procurement-header', name: <strong>Procurement & Stores Irregularities</strong>, value: '' },
          ...procurementIrregularities.map((item, index) => ({
            key: `proc-${index}`,
            name: item.name,
            value: item.value,
          })),
          {
            key: 'proc-total',
            name: <strong>Total Procurement & Stores irregularities (y)</strong>,
            value: totalProcurement,
          },
          {
            key: 'other-total',
            name: <strong>Total Other Irregularities (B = x + y)</strong>,
            value: totalOther,
          },
          {
            key: 'total-expenditure',
            name: <strong>TOTAL EXPENDITURE (C)</strong>,
            value: totalExpenditure,
          },
        ];

        setIrregularities({
          dataSource,
          cashIrreguarityPercentage: formatPercentageDisplay(cashIrreguarityPercentage),
          otherIrreguaritiesPercentage: formatPercentageDisplay(otherIrreguaritiesPercentage),
          onePercentTotalExpendature: onePercentTotalExpendature,
          threePercentTotalExpendature: threePercentTotalExpendature,
          onePercentTotalExpendatureDisplay: formatGhanaCedis(onePercentTotalExpendature),
          threePercentTotalExpendatureDisplay: formatGhanaCedis(threePercentTotalExpendature),
        });
      })
      .catch((err) => {
        console.log(err);
        setIrregularities(EMPTY_IRREGULARITIES);
        setScoreI(0);
        setScoreIII(0);
      });
  }, [districtId, year, documents]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2,
    applySnapshot: (snap) => {
      if (snap.irregularities) setIrregularities(snap.irregularities);
      if (snap.links) setDocumentLinks(snap.links);
      if (snap.scorei != null) setScoreI(snap.scorei);
      if (snap.scoreii != null) setScoreII(snap.scoreii);
      if (snap.scoreiii != null) setScoreIII(snap.scoreiii);
      if (snap.maxScore != null) setMaxScore(snap.maxScore);
    },
    deps: [year, districtId, documents, loadFromDhis2],
  });

  return (
    <Comment
      data={irregularities.dataSource}
      year={year}
      districtId={districtId}
      tableCommentedId={`pi3.0-3.2-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
            mainTitleStyle={{ marginTop: "20px" }}
          />

          <IndicatorScoreSection>
            <MaxScoreDisplay score={maxScore} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="PI 3.0-3.2i Actual Score" score={scorei} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="PI 3.0-3.2ii Actual Score" score={scoreii} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="PI 3.0-3.2iii Actual Score" score={scoreiii} />
          </IndicatorScoreSection>

          <Title level={5} style={{ marginTop: "20px" }}>
            Evidence of Auditor-General Report
          </Title>
          <Table
            columns={documentTableColumns}
            dataSource={displayDocumentLinks}
            rowKey="title"
            pagination={false}
            bordered
          />

          <Title level={5} style={{ marginTop: "20px" }}>
            Evidence of financial irregularities
          </Title>
          <Table
            columns={irregularityColumns}
            dataSource={irregularities?.dataSource || []}
            pagination={false} bordered />

          <Title level={5} style={{ marginTop: "30px" }}>Observations:</Title>
          <Content>
            <ol>
              <li>
                Percentage of cash irregularity is{" "}
                <strong>{irregularities?.cashIrreguarityPercentage ?? "0.00"}%</strong>
              </li>
              <li>
                Percentage of other irregularities is{" "}
                <strong>{irregularities?.otherIrreguaritiesPercentage ?? "0.00"}%</strong>
              </li>
              <li>
                One percent (1%) of total expenditure is{" "}
                <strong>{irregularities?.onePercentTotalExpendatureDisplay ?? "GH₵ 0.00"}</strong>
              </li>
              <li>
                Three percent (3%) of total expenditure is{" "}
                <strong>{irregularities?.threePercentTotalExpendatureDisplay ?? "GH₵ 0.00"}</strong>
              </li>
            </ol>
          </Content>
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
      )}
    </Comment>
  );
})

export default AuditInfractions;